import forge from 'node-forge';
import CryptoJS from 'crypto-js';

const EncryptionServices = {

  /**
   * function to encode password with username and iteration count when user try to login and return login object
   * @param userName
   * @param password
   * @param iterationCount
   * @returns {Promise}
   */
  generateCredential: (userName, password, iterationCount) => new Promise((resolve) => {
    // userName = userName.trim().toLowerCase();
    const encryptedPassword = EncryptionServices.generateSHA512(password, iterationCount);
    resolve({
      email: userName.trim().toLowerCase(),
      password: encryptedPassword,
    });
  }),

  generateSHA512: (str, iteration) => {
    if (iteration < 1) {
      return str;
    }
    return EncryptionServices.generateSHA512(
      CryptoJS.SHA512(str)
        .toString()
        .toLowerCase(),
      iteration - 1,
    );
  },

  /**
   * generate strong password
   */
  randomPassword: () => {
    const specialCharsString = "~`!#$%^&*+=-[]\\';,/{}|\":<>?";
    const firstRandomIndex = Math.floor(Math.random() * specialCharsString.length);
    const secondRandomIndex = Math.floor(Math.random() * specialCharsString.length);
    const randomString = Math.random().toString(36).toUpperCase().slice(2, 6);
    return Math.random().toString(36).substr(2, 6) + specialCharsString[firstRandomIndex] + specialCharsString[secondRandomIndex] + randomString;
  },

  /**
   * function to validate SSH key entered by users
   * @param sshKey
   * @returns {Promise}
   */
  checkSSHKey: (sshKey) => new Promise((resolve, reject) => {
    if (sshKey.indexOf('PRIVATE') !== -1) {
      try {
        const temp = forge.pki.privateKeyFromPem(sshKey);
        const t1 = forge.pki.privateKeyToPem(temp);
        resolve({
          error: false,
          msg: t1,
        });
      } catch (e) {
        reject({
          error: true,
          msg: 'Invalid key passed',
          err: e,
        });
      }
    } else {
      try {
        const temp = forge.pki.publicKeyFromPem(sshKey);
        const t1 = forge.pki.publicKeyToPem(temp);
        resolve({
          error: false,
          msg: t1,
        });
      } catch (e) {
        reject({
          error: true,
          msg: 'Invalid key passed',
          err: e,
        });
      }
    }
  }),

  /**
   * function to encrypt projectId with user private ssh Key
   * @param privateKey
   * @param data
   * @returns {Promise}
   */
  reEncryptProjectKeysWithPrivKey: (privateKey, data) => new Promise((resolve, reject) => {
    const tempPrivateProjectKeyArray = [];
    data.map((projectData, projectIndex) => {
      EncryptionServices.decryptDataRSA(projectData.symmetricKey).then(
        (decryptedKey) => {
          const tempProjectIns = {};
          let tempPrivateData = '';
          try {
            const myPk = forge.pki.privateKeyFromPem(privateKey);
            const encrypted = forge.pki.rsa.encrypt(
              forge.util.encodeUtf8(decryptedKey),
              myPk,
              0x01,
            );
            tempPrivateData = forge.util.bytesToHex(encrypted);

            tempProjectIns.project_id = projectData.projectId;
            tempProjectIns.symkey = forge.util.encode64(tempPrivateData);

            tempPrivateProjectKeyArray.push(tempProjectIns);

            if (projectIndex + 1 === data.length) {
              resolve({
                status: true,
                privProjectKey: tempPrivateProjectKeyArray,
              });
            }
          } catch (error) {
            reject({
              status: false,
              privProjectKey: null,
            });
          }
        },
        () => {
          reject({
            status: false,
            privProjectKey: null,
          });
        },
      );
      return true;
    });
  }),

  /**
   * function to encrypt plain text for http request
   * @param plaintext
   * @param key
   * @param v
   * @returns {Promise}
   */
  encryptDataAES: (plaintext, key, v) => new Promise((resolve, reject) => {
    try {
      const cipher = forge.cipher.createCipher('AES-CBC', key);
      cipher.start({ iv: v });
      cipher.update(forge.util.createBuffer(plaintext));
      cipher.finish();
      resolve(forge.util.encode64(cipher.output.data));
    } catch (error) {
      reject(error);
    }
  }),

  /**
   * function to decrypt data to plain text
   * @param ciphertext
   * @param key
   * @param v
   * @returns {Promise}
   */
  decryptDataAES: (ciphertext, key, v) => new Promise((resolve, reject) => {
    try {
      const cipher = forge.util.decode64(ciphertext);
      const decipher = forge.cipher.createDecipher('AES-CBC', key);
      decipher.start({ iv: v });
      decipher.update(forge.util.createBuffer(cipher));
      decipher.finish();
      resolve(decipher.output.data);
    } catch (error) {
      reject(error);
    }
  }),

  /**
   * function to encrypt plain text using user public key
   * @param message
   * @returns {Promise}
   */
  encryptDataRSA: (message, key) => new Promise((resolve, reject) => {
    try {
      let serverPublicKey;
      if (key) {
        serverPublicKey = forge.pki.publicKeyFromPem(key);
      } else {
        serverPublicKey = forge.pki.publicKeyFromPem(sessionStorage._clientpk);
      }
      const plaintextBytes = forge.util.encodeUtf8(message);
      const encrypted = serverPublicKey.encrypt(plaintextBytes);
      resolve(forge.util.encode64(encrypted));
    } catch (error) {
      reject(error);
    }
  }),

  /**
   * function to decrypt data using user private key
   * @param b64cipher
   * @returns {Promise}
   */
  decryptDataRSA: (b64cipher) => new Promise((resolve, reject) => {
    try {
      const msgDecode = forge.util.decode64(b64cipher);
      const privateKey = forge.pki.privateKeyFromPem(sessionStorage.getItem('_myPk'));
      const decrypted = privateKey.decrypt(msgDecode);
      resolve(forge.util.decodeUtf8(decrypted));
    } catch (error) {
      reject(error);
    }
  }),

  /**
   * function to encode data before sending to server
   * @param data
   * @param multipleEncryption
   * @returns {Promise}
   */
  processDataToServer: (data, multipleEncryption) => new Promise((resolve, reject) => {
    const message = data.data;
    const { projectId } = data;

    if (multipleEncryption) {
      const initVector = data && data.v ? forge.util.decode64(data.v) : forge.random.getBytesSync(16);
      const sk = JSON.parse(sessionStorage.getItem('_ppak'));

      /* for (const k in sk) {
        if (parseInt(sk[k].projectId) === parseInt(projectId)) {
          EncryptionServices.decryptDataRSA(sk[k].symmetricKey).then((decryptedKey) => {
            EncryptionServices.encryptDataAES(message, decryptedKey, initVector)
              .then((encryptedDataAES) => {
                resolve({
                  encrypted: encryptedDataAES,
                  v: forge.util.encode64(initVector),
                  projectId,
                  status: true,
                });
              })
              .catch((error) => {
                reject({
                  error,
                  status: false,
                });
              });
          });
        }
      } */

      Object.keys(sk).forEach((k) => {
        if (parseInt(sk[k].projectId) === parseInt(projectId)) {
          EncryptionServices.decryptDataRSA(sk[k].symmetricKey).then((decryptedKey) => {
            EncryptionServices.encryptDataAES(message, decryptedKey, initVector)
              .then((encryptedDataAES) => {
                resolve({
                  encrypted: encryptedDataAES,
                  v: forge.util.encode64(initVector),
                  projectId,
                  status: true,
                });
              })
              .catch((error) => {
                reject({
                  error,
                  status: false,
                });
              });
          });
        }
      });
    } else {
      EncryptionServices.encryptDataRSA(message)
        .then((encryptedDataRSA) => {
          resolve({
            encrypted: encryptedDataRSA,
            status: true,
          });
        })
        .catch((error) => {
          reject({
            error,
            status: false,
          });
        });
    }
  }),

  /**
   * function to decode data coming from http request
   * @param data
   * @param multipleEncryption
   * @returns {Promise}
   */
  processDataFromServer: (data, multipleEncryption) => new Promise((resolve, reject) => {
    const message = data.encrypted;
    const { projectId } = data;
    const initVector = forge.util.decode64(data.v);
    if (multipleEncryption) {
      const sk = JSON.parse(sessionStorage.getItem('_ppak'));

      /* for (const k in sk) {
        if (parseInt(sk[k].projectId) === parseInt(projectId)) {
          EncryptionServices.decryptDataRSA(sk[k].symmetricKey).then((decryptedKey) => {
            EncryptionServices.decryptDataAES(message, decryptedKey, initVector)
              .then((decryptedData) => {
                resolve(decryptedData);
              })
              .catch((error) => {
                reject({ error });
              });
          });
        }
      } */

      Object.keys(sk).forEach((k) => {
        if (parseInt(sk[k].projectId) === parseInt(projectId)) {
          EncryptionServices.decryptDataRSA(sk[k].symmetricKey).then((decryptedKey) => {
            EncryptionServices.decryptDataAES(message, decryptedKey, initVector)
              .then((decryptedData) => {
                resolve(decryptedData);
              })
              .catch((error) => {
                reject({ error });
              });
          });
        }
      });
    } else {
      EncryptionServices.decryptDataRSA(message)
        .then((info) => {
          resolve(info);
        })
        .catch((error) => {
          reject({ error });
        });
    }
  }),

  /**
   * CHECK IF private and public key match or not
   * @param privKey
   * @param pubKey
   * @returns {Promise<any>}
   */
  validateWithPrivKey: (privKey, pubKey) => new Promise((resolve, reject) => {
    try {
      const msg = 'penportal';
      const serverPublicKey = forge.pki.publicKeyFromPem(pubKey);
      const plaintextBytes = forge.util.encodeUtf8(msg);
      const encrypted = serverPublicKey.encrypt(plaintextBytes);
      const privateKey = forge.pki.privateKeyFromPem(privKey);
      const decrypted = privateKey.decrypt(encrypted);
      resolve(forge.util.decodeUtf8(decrypted));
    } catch (error) {
      reject(error);
    }
  }),

  generateAESSecretKey() {
    return new Promise((resolve) => {
      const randomKey = forge.random.getBytesSync(32);
      const serverPublicKey = forge.pki.privateKeyFromPem(sessionStorage.getItem('_myPk'));
      let encrypted = forge.pki.rsa.encrypt(forge.util.encodeUtf8(randomKey), serverPublicKey, 0x01);
      encrypted = forge.util.bytesToHex(encrypted);
      resolve({
        project_id: 0,
        symkey: forge.util.encode64(encrypted),
      });
    });
  },

  decodeJwtToken(token) {
    return new Promise((resolve) => {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
          .join(''),
      );
      resolve(JSON.parse(jsonPayload));
    });
  },

  base64ToHex(base64) {
    return forge.util.bytesToHex(base64);
  },
  hexToBase64(hex) {
    return forge.util.hexToBytes(hex);
  },

  encode64(text) {
    return forge.util.encode64(text);
  },
  decode64(base64) {
    return forge.util.decode64(base64);
  },

};

export default EncryptionServices;
